/**
 * BoardSnapshotCard -- Home tile for admin OR approver
 * (P17.3 / #458).
 *
 * Compact preview: overall RAG chip + revenue at risk + a "Open
 * board" link to /board. Non-admin / non-approver users see
 * nothing (mirrors DossierCard's visibility pattern).
 *
 * Doesn't render the full table or trend -- this is a board
 * shortcut, not the board page itself.
 */
import { useTranslation } from "react-i18next";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { canReadBoard } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import type { Rag } from "shared/api/types";
import { ragCardStatus, ragPillTone, ragWordKey } from "shared/board/ragTone";
import { BrassLink } from "shared/components/BrassLink";
import { Card, CardError } from "shared/components/Card";
import { StatusPill } from "shared/components/StatusPill";
import { formatSAR } from "shared/money/money";

import { useBoardSnapshot } from "./useBoardSnapshot";

export function BoardSnapshotCard() {
  const { t } = useTranslation();
  const me = useMe();
  const canSee = canReadBoard(me.data);
  // Ask only when we may be answered -- see the hook's `enabled` note.
  const query = useBoardSnapshot({ enabled: canSee });
  if (!me.data || !canSee) {
    return null;
  }

  if (query.isLoading) {
    return (
      <Card
        testid="board-card-loading"
        micro={t("modules.board.card.heading")}
        icon="dossier"
      >
        <p className="mt-2 text-sm text-ink-muted">{t("modules.board.card.loading")}</p>
      </Card>
    );
  }

  if (query.isError) {
    // P125.2 (brand P2.10): a calm visible "load failed" state -- the
    // full page at /board still surfaces the structured error inline.
    return (
      <CardError
        testid="board-card-error"
        micro={t("modules.board.card.heading")}
        icon="dossier"
        message={t("modules.board.card.error")}
      />
    );
  }
  if (!query.data) {
    return null;
  }

  const h = query.data.headline;
  const rag: Rag = h.overall_rag;

  return (
    <Card
      testid="board-snapshot-card"
      status={ragCardStatus(rag)}
      className="space-y-3"
    >
      <header className="flex items-baseline justify-between gap-2">
        <p className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-muted">
          <MarsadIcon name="dossier" size={13} className="text-brand-brassDark" />
          {t("modules.board.card.heading")}
        </p>
        {/* Round 12 B1 -- this printed the raw value, so a fourth
            state would have read "not_assessed" at a board. The word
            comes from the catalogue now, and the tone is neutral
            because no colour is honest over an estate nobody has
            looked at. */}
        <StatusPill testid="board-card-rag" tone={ragPillTone(rag)} data-rag={rag}>
          {t(`modules.board.ragWord.${ragWordKey(rag)}`)}
        </StatusPill>
      </header>
      <p className="text-[12.5px] text-ink-2 tabular-nums">
        {t("modules.board.card.summary", {
          impacted: h.ibs_impacted,
          total: h.ibs_count,
        })}
      </p>
      <p className="text-[12.5px] text-ink-2 tabular-nums">
        {t("modules.board.card.revenue", {
          annual: formatSAR(h.revenue_at_risk_annual),
        })}
      </p>
      <BrassLink to="/board" testid="board-card-open" className="inline-block">
        {t("modules.board.card.open")}
      </BrassLink>
    </Card>
  );
}
